import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  styled,
  Tab,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';
import { Close, Image, Sort, Title, Save, Add } from '@mui/icons-material';

import {
  DragDropContext,
  Droppable,
  Draggable,
  DroppableProvided,
  DraggableProvided,
} from 'react-beautiful-dnd';

import { COLORS } from '../../../../utils/colors';
import { StyledSideTabs } from '../../../../components/StyledTabs';
import { useAIDocumentEditorContext } from '../../../../contexts/AIDocumentEditorContext';
import { useBookingsContext } from '../../../../contexts/BookingsContext';
import {
  IForm,
  IPromptCard,
  ITextBlock,
  IUserDocumentSection,
  IUserDocumentTemplate,
} from '@homeporter/types';

import { useDocEditorPagesStyles } from './styles';

const DocumentEditorSections = () => {
  const styles = useDocEditorPagesStyles();
  const {
    documentsRef,
    saveTemplateButtonRef,
    documentIndex,
    documentSections,
    setDocumentSections,
    documentSection,
    setDocumentSection,
    setSaveEnabled,
  } = useAIDocumentEditorContext();

  const {
    profile,
    userSettings,
    context,
    documentTemplates,
    updateDocumentTemplate,
  } = useBookingsContext();

  const [view, setView] = useState(0);
  const previewBoxRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState<number>(1);

  const [isSaveTemplateModalOpen, setIsSaveTemplateModalOpen] = useState(false);
  const [templateTitle, setTemplateTitle] = useState('custom');
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setError] = useState<string | null>(null);

  const handleChangeView = (event: any) => {
    setView(event.target.value);
  };

  const addSection = () => {
    if (!documentsRef.current) return;

    const newSection: IUserDocumentSection = {
      index: documentSections.length,
      deleted: false,
      context,
      cards: [],
    };

    documentsRef.current[documentIndex].sections.push(newSection);
    setDocumentSections([...documentsRef.current[documentIndex].sections]);
    console.log('::::setDocument section 9...');
    setDocumentSection(newSection);
    setSaveEnabled(true);
  };

  const compareAndUpdateSection = () => {
    console.log(
      '::::inside compareAndUpdateSection, documentSection: ' + documentIndex,
      documentSection,
      documentsRef.current?.[documentIndex],
    );

    if (
      !documentSection?.index ||
      !documentsRef.current?.[documentIndex]?.sections?.[
        documentSection.index
      ] ||
      JSON.stringify(
        documentsRef.current[documentIndex].sections[documentSection.index],
      ) === JSON.stringify(documentSection)
    )
      return;

    setSaveEnabled(true);
    documentsRef.current[documentIndex].sections[documentSection.index] =
      documentSection;
  };

  const handleSelectSection = (index: number) => {
    console.log(':::::handleSelectSection documentSection:', documentSection);

    compareAndUpdateSection();
    console.log('::::setDocument section 10...');
    setDocumentSection(documentSections[index] || null);
  };

  const handleRemoveSection = (_: number) => {
    if (!documentSection || !documentsRef.current) return;

    documentsRef.current[documentIndex].sections[
      documentSection.index
    ].deleted = true;

    const remainingSections = documentsRef.current[
      documentIndex
    ].sections.filter(
      (section, idx) => !section.deleted && idx !== documentSection.index,
    );

    const nextSection =
      remainingSections.find((s) => s.index > documentSection.index) ||
      remainingSections[remainingSections.length - 1] ||
      null;

    setDocumentSections([...documentsRef.current[documentIndex].sections]);
    console.log('::::setDocument section 11...');
    setDocumentSection(nextSection);
    setSaveEnabled(true);
  };

  const handleDragEnd = (result: any) => {
    if (!result.destination) return;

    const items = Array.from(documentSections);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setDocumentSections(items);
    setSaveEnabled(true);
  };

  const saveDocumentTemplateModal = useCallback(() => {
    const doc = documentsRef.current?.[documentIndex ?? -1];
    if (!doc) return;

    setIsSaving(false);
    setTemplateTitle(doc.title ?? '');
    setError(null);
    setIsSaveTemplateModalOpen(true);
  }, [documentIndex]);

  saveTemplateButtonRef.current = saveDocumentTemplateModal;

  const documentTemplateFromDocument = (currentDoc: IUserDocumentTemplate) => {
    console.log('::::documentTemplateFromDocument currentDoc: ', currentDoc);

    const documentTemplate = {
      dtid: 0,
      version: currentDoc.version,
      title: currentDoc.title,
      template_name: currentDoc.template_name,
      user_id: profile?.user_id || 0,
      organization_id: 0,
      sections: [] as IUserDocumentSection[],
    };

    currentDoc.sections.forEach((section: IUserDocumentSection) => {
      if (section.deleted) return;

      let sort = 1;
      const cards = section.cards
        .filter((card) => card.include)
        .sort((a, b) => a.sort - b.sort)
        .map((card) => {
          if (card.type === 'Prompt') {
            const { message_id, id, ...restBaseCard } = card as IPromptCard;
            return {
              ...restBaseCard,
              context: '',
              response: '',
              modified: '',
              images: [],
              undo: [],
              redo: [],
              auto: false,
              sort: sort++,
            };
          } else if (card.type === 'TextBlock') {
            const { id, ...restBaseCard } = card as ITextBlock;
            return {
              ...restBaseCard,
              redo: [],
              undo: [],
              sort: sort++,
            };
          } else if (card.type === 'Form') {
            const { message_id, id, ...restBaseCard } = card as IForm;
            return {
              ...restBaseCard,
              context: '',
              data: [],
              sort: sort++,
            };
          }
          return card;
        });

      documentTemplate.sections.push({ ...section, context: '', cards });
    });

    return documentTemplate;
  };

  const handleSaveTemplate = async (isNew: boolean = false) => {
    try {
      const currentDoc = documentsRef?.current?.[documentIndex];
      if (!currentDoc) return;

      const documentTemplate = documentTemplateFromDocument(currentDoc);
      if (!isNew) documentTemplate.dtid = currentDoc.dtid;
      documentTemplate.title = templateTitle;
      documentTemplate.template_name = templateTitle
        .toLowerCase()
        .replace(/ /g, '-');

      console.log(
        '::::::handleSaveTemplate, documentTemplate:',
        documentTemplate,
      );

      setIsSaving(true);
      await updateDocumentTemplate(documentTemplate);
      setIsSaveTemplateModalOpen(false);
    } catch (error: any) {
      console.error('Save template error:', error);
      setError(
        error?.response?.data?.message ||
          error?.message ||
          'Failed to save template',
      );
      setIsSaving(false);
    }
  };
  /*
  useEffect(() => {
    if (previewBoxRef.current && documentRef.current) {
      const previewBoxWidth = previewBoxRef.current.offsetWidth;
      const previewBoxHeight = previewBoxRef.current.offsetHeight;
      const contentWidth = documentRef.current.scrollWidth;
      const contentHeight = documentRef.current.scrollHeight;

      const scaleX = previewBoxWidth / contentWidth;
      const scaledHeight = contentHeight * scaleX;
      const scaleY = previewBoxHeight / scaledHeight;

      setScale(Math.min(scaleX, scaleY));
    }
  }, [documentRef, documentSection?.cards]);
*/
  //if only one section, minimize left column and just have + icon
  const isOneSection =
    documentSections.filter((section) => !section.deleted).length === 1;

  const divStyles = Object.entries(styles.previewBoxInner)
    .map(
      ([key, value]) =>
        `${key.replace(/([A-Z])/g, '-$1').toLowerCase()}: ${value}`,
    )
    .join(';');

  const isNewTemplate = !documentTemplates?.some(
    (template) => template.user_id !== 0 && template.title === templateTitle,
  );

  const isNewTitle = !documentTemplates?.some(
    (template) => template.title === templateTitle,
  );

  return (
    <>
      {userSettings?.dev ? (
        <Box
          id="document-editor-sections"
          sx={{
            ...styles.container,
            width: isOneSection ? '3rem' : '11rem',
          }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
            }}>
            <Box margin={'1rem'} textAlign={'center'}>
              <Button
                onClick={addSection}
                sx={{
                  ...styles.addSectionButton,
                  minWidth: '0',
                  padding: isOneSection ? '.25rem' : '.25rem 1rem',
                  width: isOneSection ? 'auto' : '100%',
                }}
                variant="outlined">
                <Add />
                {isOneSection ? '' : ' Section'}
              </Button>
            </Box>

            {!isOneSection && (
              <Box sx={{ flex: 1, overflowY: 'auto' }}>
                <DragDropContext onDragEnd={handleDragEnd}>
                  <Droppable droppableId="sections">
                    {(provided: DroppableProvided) => (
                      <Box
                        {...provided.droppableProps}
                        ref={provided.innerRef}
                        sx={{ ...styles.sectionsContainer, overflowY: 'auto' }}>
                        {documentSections
                          .map((section, index) => ({ section, index }))
                          .filter(({ section }) => !section.deleted)
                          .map(({ section, index }, visibleIndex) => (
                            <Draggable
                              key={`section-${section.index}-${visibleIndex}`}
                              draggableId={section.index.toString()}
                              index={index}>
                              {(provided: DraggableProvided) => (
                                <Box
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  {...provided.dragHandleProps}
                                  onClick={() => handleSelectSection(index)}
                                  sx={styles.section}
                                  border={
                                    documentSection?.index === section.index
                                      ? `2px solid #53617a`
                                      : `1px solid ${COLORS.MEDIUM_GRAY}`
                                  }>
                                  {index > 0 &&
                                    documentSection?.index ===
                                      section.index && (
                                      <IconButton
                                        onClick={() =>
                                          handleRemoveSection(index)
                                        }
                                        sx={styles.closeButton}>
                                        <Close fontSize="small" />
                                      </IconButton>
                                    )}
                                  <div
                                    ref={previewBoxRef}
                                    style={
                                      styles.previewBox as React.CSSProperties
                                    }>
                                    <div
                                      dangerouslySetInnerHTML={{
                                        __html: `<div style="${divStyles}"><b>Section ${visibleIndex + 1}</b>
                                        ${
                                          section.cards.filter(
                                            (card) => card.include,
                                          ).length === 0
                                            ? `<div style="font-size: 0.8em; margin-top: 4px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; width: 100%;">&lt;Empty&gt;</div>`
                                            : section.cards
                                                .filter((card) => card.include)
                                                .map(
                                                  (card) =>
                                                    `<div style="font-size: 0.8em; margin-top: 4px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; width: 100%;">${card.title}</div>`,
                                                )
                                                .join('')
                                        }
                                        </div>`,
                                      }}
                                      style={
                                        {
                                          transform: `scale(${scale})`,
                                        } as React.CSSProperties
                                      }
                                    />
                                  </div>
                                </Box>
                              )}
                            </Draggable>
                          ))}
                        {provided.placeholder}
                      </Box>
                    )}
                  </Droppable>
                </DragDropContext>
              </Box>
            )}
          </Box>
        </Box>
      ) : (
        <Box
          sx={{
            ...styles.container,
            width: '.5rem',
            margin: '0',
            padding: '0',
          }}></Box>
      )}

      <Dialog
        open={isSaveTemplateModalOpen}
        onClose={() => setIsSaveTemplateModalOpen(false)}>
        <DialogTitle>Save Template</DialogTitle>
        <DialogContent sx={{ width: '36rem' }}>
          <TextField
            autoFocus
            margin="dense"
            label="Template Title"
            type="text"
            fullWidth
            variant="outlined"
            value={templateTitle}
            onChange={(e) => setTemplateTitle(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsSaveTemplateModalOpen(false)}>
            Cancel
          </Button>
          {saveError && (
            <Typography color="error" variant="caption" sx={{ mr: 2 }}>
              {saveError}
            </Typography>
          )}
          {!saveError && (
            <Button
              onClick={() => handleSaveTemplate(isNewTemplate)}
              disabled={
                !templateTitle.trim() ||
                isSaving ||
                (isNewTemplate && !isNewTitle)
              }>
              {!isNewTemplate
                ? isSaving
                  ? 'Updating...'
                  : 'Update'
                : isSaving
                  ? 'Saving...'
                  : 'Save New'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DocumentEditorSections;
